import { Component, OnInit, Input, OnDestroy, ViewChild, AfterViewInit, ChangeDetectionStrategy, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { NachrichtService } from 'src/app/api/nachricht.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SystemService } from 'src/app/api/system.service';
import { IonToolbar, NavController, MenuController, IonBackButton, IonMenuButton, IonHeader } from '@ionic/angular';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { AuftragService } from 'src/app/api/auftrag.service';
import { MyApp } from 'src/app/api/helper/app';
import { AppConfig } from 'src/app/api/helper/app.config';
import { ClickHelper } from 'src/app/api/helper/click-helper';
import { GeraetStatusTyp, TourStatusEx } from 'src/app/api/model/model';
import { DruckerService } from 'src/app/api/drucker.service';
import { WaageService } from 'src/app/api/waage.service';
import { Utils } from 'src/app/api/helper/utils';
import { ProgloveService } from 'src/app/api/proglove.service';
import { Zeiterfassung } from 'src/app/api/helper/zeiterfassung-helper';

const log = new Logger("MainmenuComponent");

@Component({
    selector: 'app-mainmenu',
    templateUrl: './mainmenu.component.html',
    styleUrls: ['./mainmenu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MainmenuComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('menuButton', { read: IonMenuButton, static: false }) menuButton: IonMenuButton;
    @ViewChild('backButton', { read: IonBackButton, static: false }) backButton: IonBackButton;
    @ViewChild('toolbar', { read: IonToolbar, static: false }) toolbar: IonToolbar;
    @ViewChild('header', { read: IonHeader, static: false }) header: IonHeader;

    @Input() title: string;
    @Input() back: boolean;
    @Input() backUrl: string;
    @Input() menuButtonVisible: boolean;

    /** Optionaler Zusatztext rechts neben dem Titel, z.B. "12 offen · 3 erledigt". */
    @Input() zaehlerText = '';

    @Input() loeschenAnzeigen = false;
    @Input() druckerAnzeigen = true;
    @Input() waageAnzeigen = true;
    @Input() nachrichtenAnzeigen = true;
    @Input() progoveAnzeigen = true;
    @Input() sync = true;

    @Output() showMenu: EventEmitter<any> = new EventEmitter();
    @Output() loeschen: EventEmitter<any> = new EventEmitter();

    app = MyApp.current;

    currentColor = 'secondary';
    color: string;
    anzahlNeueNachrichten: BehaviorSubject<number>;
    anzahlWiegungen: BehaviorSubject<number>;
    anzahlAktuelleTransportauftraege: BehaviorSubject<number>;
    anzahlAktuelleHofcheckerAuftraege: BehaviorSubject<number>;
    druckerColor = new BehaviorSubject<string>('danger');
    waageColor = new BehaviorSubject<string>('danger');
    progloveColor = new BehaviorSubject<string>('danger');
    isVisible = false;
    subscriptions: Subscription[] = [];
    zeiterfassungAnzeigen = false;
    zeiterfassungColor = 'light';

    syncSymbolAnzeigen = false;

    constructor(
        private nachrichtService: NachrichtService,
        private auftragService: AuftragService,
        private systemService: SystemService,
        private druckerService: DruckerService,
        private waageService: WaageService,
        private proGloveService: ProgloveService,
        private menuCtrl: MenuController,
        private cdr: ChangeDetectorRef,
        private nav: NavController) {

        this.anzahlNeueNachrichten = this.nachrichtService.anzahlNeueNachrichten;
        this.anzahlAktuelleTransportauftraege = this.auftragService.anzahlAktuelleTransportauftraege;
        this.anzahlAktuelleHofcheckerAuftraege = this.auftragService.anzahlAktuelleHofcheckerAuftraege;
        this.anzahlWiegungen = this.waageService.anzahlWiegungen;
    }

    async ngOnInit() {
        this.subscriptions.push(AppConfig.changed.subscribe(() => this.updateView()));
        this.subscriptions.push(MyApp.current.anzahlPostausgangNachrichten.subscribe(() => this.updateView()));

        this.subscriptions.push(this.druckerService.druckerStatus.subscribe(status => {
            switch (status) {
                case GeraetStatusTyp.Unbekannt:
                    this.druckerColor.next('light');
                    break;
                case GeraetStatusTyp.NichtVerbunden:
                    this.druckerColor.next('danger');
                    break;
                case GeraetStatusTyp.Suche:
                    this.druckerColor.next('warning');
                    break;
                case GeraetStatusTyp.Verbunden:
                    this.druckerColor.next('success');
                    break;
                case GeraetStatusTyp.WirdGedruckt:
                    this.druckerColor.next('primary');
                    break;
                default:
                    this.druckerColor.next('danger');
                    break;
            }
        }));

        this.subscriptions.push(this.waageService.waageStatus.subscribe(status => {
            log?.debug('WaageStatus: ' + status);

            const verbindungsmodus = AppConfig.current.einstellungen?.WaageVerbindungsModus;

            switch (status) {
                case GeraetStatusTyp.Unbekannt:
                    this.waageColor.next('light');
                    break;
                case GeraetStatusTyp.NichtVerbunden:
                    // Bei der PFREUNDT-Waage wird die Verbindung nur beim Klick auf den Wiegen-Button hergestellt.
                    // Es ist also ganz normal, dass die Waage nicht verbunden ist. Das soll nicht als Fehler angezeigt werden.
                    if (verbindungsmodus === 'nur-bei-bedarf' || AppConfig.current?.einstellungen?.WaagenTyp === 'pfreundt') {
                        this.waageColor.next('light');
                    } else {
                        this.waageColor.next('danger');
                    }
                    break;
                case GeraetStatusTyp.Suche:
                    this.waageColor.next('warning');
                    break;
                case GeraetStatusTyp.Verbunden:
                    this.waageColor.next('success');
                    break;
                case GeraetStatusTyp.WirdGedruckt:
                    this.waageColor.next('primary');
                    break;
                default:
                    this.waageColor.next('danger');
                    break;
            }
        }));

        this.subscriptions.push(this.proGloveService.status.subscribe(status => {
            switch (status) {
                case 'GETRENNT':
                    this.progloveColor.next('danger');
                    break;
                case 'SUCHE':
                    this.progloveColor.next('warning');
                    break;
                case 'WIRD VERBUNDEN':
                    this.progloveColor.next('primary');
                    break;
                case 'VERBUNDEN':
                    this.progloveColor.next('success');
                    break;
                default:
                    this.progloveColor.next('danger');
                    break;
            }
        }));

        this.subscriptions.push(Zeiterfassung.changed.subscribe(() => this.updateView()));
        this.subscriptions.push(MyApp.current.aktuelleTour.subscribe(() => this.updateView()));

        this.isVisible = true;
    }

    ngOnDestroy() {
        this.isVisible = false;

        this.subscriptions.forEach(p => p.unsubscribe());
        this.subscriptions = [];
    }

    ngAfterViewInit() {
        log?.debug('ngAfterViewInit');

        if (!this.app.TitelleisteAnzeigen.getValue()) {
            return;
        }

        this.updateView();

        setTimeout(() => {
            if (!this.isVisible) {
                return;
            }

            try {
                const element = document.getElementById('page-title-span');
                element.click();

                // if (this.backButton) {
                //     this.focusButton('mainmenu-back-button');
                // } else if (this.menuButton) {
                //     this.focusButton('mainmenu-menu-button');
                // } else {
                //     log.warn('focus NONE');
                // }
            } catch (err) {
                log.error('ngAfterViewInit: ' + err.message, err);
            }
        }, 100);
    }

    focusButton(id: string) {
        log?.debug('focus button: ' + id);

        const element = document.getElementById(id);

        if (!element) {
            log?.debug('element not found: ' + id);
            return;
        }

        let button;

        if (element.shadowRoot) {
            log?.debug('element has shadowRoot');
            button = element.shadowRoot.querySelector('button');
        } else {
            log?.debug('element does not have a shadowRoot');
            button = element.querySelector('button');
        }

        if (button) {
            button.focus();
        } else {
            log?.debug('no button found', element);
        }
    }

    async updateView() {
        if (!this.isVisible) {
            return;
        }

        const appConfig = await this.systemService.getAppConfig();

        if (appConfig.einstellungen) {
            if (appConfig.einstellungen.Farbe1) {
                if (this.toolbar) {
                    const toolbarElement = (this.toolbar as any).el;
                    toolbarElement.style.setProperty('--background', appConfig.einstellungen.Farbe1);
                } else {
                    log.warn('toolbar nicht gefunden');
                }
            } else {
                log.warn('einstellungen.Farbe1 is NULL');
            }

            this.updateZeiterfassungStatus();

            this.syncSymbolAnzeigen = this.app.anzahlPostausgangNachrichten.getValue() > 0;

            this.cdr.detectChanges();
        }
    }

    updateZeiterfassungStatus() {
        const zeiterfassungVerfuegabr = this.app.tourGestartet.getValue() && AppConfig.current.einstellungen.TourZeiterfassungAktiv === 'ja';

        this.zeiterfassungColor = 'light';
        this.zeiterfassungAnzeigen = false;

        if (zeiterfassungVerfuegabr) {
            const aktuelleTour = MyApp.current.aktuelleTour.getValue() as TourStatusEx;

            if (aktuelleTour) {
                let zeiterfassungLaeuft = !!aktuelleTour.Zeiterfassung?.Eintraege?.find(p => !p.EndDatum);

                if (zeiterfassungLaeuft) {
                    this.zeiterfassungColor = 'light';
                } else {
                    // Symbol nur anzeigen, wenn die Zeiterfassung nicht gestartet ist
                    this.zeiterfassungColor = 'warning';
                    this.zeiterfassungAnzeigen = true;
                }
            } else {
                // Keine Tout
                this.zeiterfassungAnzeigen = false;
            }
        }
    }

    goBack(e: Event) {
        log?.debug('goBack. isVisible=' + this.isVisible);

        if (this.isVisible) {
            this.nav.back();
        }
    }

    toggleMenu() {
        log?.debug('toggleMenu');
        this.menuCtrl.open();
    }

    onShowMenu(event: any) {
        log?.debug('onShowMenu');
        this.showMenu.next(event);
    }

    onLoeschen(event: any) {
        log?.debug('onLoeschen');
        this.loeschen.next(event);
    }
}
